import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@lib/utils';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  footer?: string;
  variant: 'purple' | 'orange' | 'green' | 'red';
}

const variantStyles = {
  purple: {
    iconColor: 'text-[var(--text-accent)]',
  },
  orange: {
    iconColor: 'text-[var(--state-warning)]',
  },
  green: {
    iconColor: 'text-[var(--state-success)]',
  },
  red: {
    iconColor: 'text-[var(--state-danger)]',
  },
};

export const StatCard = ({ label, value, icon: Icon, trend, footer, variant }: StatCardProps) => {
  const styles = variantStyles[variant];

  return (
    // Surface token, not literal white: in the dark theme a hardcoded white
    // card is a glowing slab on a near-black page. Light theme is unchanged
    // (--bg-surface is #ffffff there).
    <div className="bg-[var(--bg-surface)] rounded-3xl p-6 shadow-soft hover:shadow-lift hover:-translate-y-0.5 transition-all duration-300">
      {/* Top row: icon + trend pill */}
      <div className="flex items-start justify-between">
        <div className="rounded-2xl p-2.5 bg-[var(--bg-base)]">
          <Icon className={cn('w-5 h-5', styles.iconColor)} strokeWidth={1.5} />
        </div>

        {trend && (
          <div
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold',
              trend.isPositive
                ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                : 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]'
            )}
          >
            {trend.isPositive ? (
              <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} />
            ) : (
              <TrendingDown className="w-3.5 h-3.5" strokeWidth={1.5} />
            )}
            {trend.value}%
          </div>
        )}
      </div>

      {/* Value */}
      <div className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-4 leading-none">
        {value}
      </div>

      {/* Label */}
      <div className="text-[13px] font-medium text-[var(--text-muted)] mt-1">{label}</div>

      {/* Footer */}
      {footer && (
        <div className="text-[12px] text-[var(--text-muted)] mt-4 pt-4 border-t border-[var(--border-subtle)]">
          {footer}
        </div>
      )}
    </div>
  );
};